import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import AdyenCheckout from '@adyen/adyen-web';
import { PaymentMethod } from '@adyen/adyen-web/dist/types/types';
import Button from 'components/commercetools-ui/atoms/button';
import '@adyen/adyen-web/dist/adyen.css';
import Link from 'components/commercetools-ui/atoms/link';
import Message from 'components/commercetools-ui/atoms/message';
import Radio from 'components/commercetools-ui/atoms/radio';
import Skeleton from 'components/commercetools-ui/atoms/skeleton';
import { useAdyenCheckout, useCheckout } from 'components/commercetools-ui/organisms/checkout/provider';
import { PaymentType } from 'components/commercetools-ui/organisms/checkout/provider/payment/types';
import V12Image from 'components/icons/v12-finance';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import aggregatedQuantity from 'helpers/utils/aggregatedQuantity';
import { useAdyen, useCart, useStock, useAccount, useV12 } from 'frontastic';
import Givex from './components/givex';
import V12 from './components/v12';
import PaymentHelper from './helper';
import { useSearchParams } from 'next/navigation';

interface PaymentMethodDetail {
  name: string;
  image: { src: string };
}

const paymentMethodDetails: { [key: string]: PaymentMethodDetail } = {
  scheme: {
    name: 'Credit or Debit Card',
    image: { src: 'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/card.svg' },
  },
  v12: {
    name: 'V12 Retail Finance',
    image: {
      src: '/images/v12.svg',
    },
  },
};

interface Props {
  goToNextStep: () => void;
  isActive: boolean;
  makeActive: () => void;
}

const Payment: React.FC<Props> = ({ goToNextStep, isActive, makeActive }) => {
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { getCountry, getCurrency, locale } = useLocale();
  const adyen = useAdyen();
  const { data: cartData, productSKUs } = useCart();
  const { account, loggedIn } = useAccount();
  const { fluentStocks, isStockChecked, recheckStocks } = useStock();
  const router = useRouter();
  const { setHasOutOfStockItems, hasOutOfStockItems, scrollToError, previousPaymentMethod, setPreviousPaymentMethod } =
    useCheckout();
  const [isLoading, setIsLoading] = useState(false);
  const paymentHelper = PaymentHelper();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[] | null>(null);
  const { isV12PaymentEnabledForCart } = useV12();
  const [isV12Enabled, setIsV12Enabled] = useState(false);
  const searchParams = useSearchParams();

  const giftCards = cartData?.payments?.filter((item) => {
    return item.paymentProvider === 'giftcard';
  });

  const refusedMessage = `Unfortunately, we were unable to take payment for this order and no payment has been taken. ${
    giftCards && giftCards?.length >= 2
      ? 'Please review your chosen payment methods.'
      : 'Please try again using a different payment method.'
  }`;

  const {
    setAdyenCheckoutInstance,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentStatus,
    setPaymentStatus,
    setProcessing,
    setV12PaymentData,
    setMountedComponent,
    setPaymentSession,
    isPaymentDataValid,
    setIsPaymentDataValid,
    setAdyenCheckoutInstanceState,
  } = useAdyenCheckout();

  const isProductsHasOutOfStocks = useMemo(() => {
    if (isStockChecked && fluentStocks.length > 0) {
      const isInStock = cartData?.lineItems?.some((item) => {
        if (item.isPerpetualStock) {
          return;
        }
        const availableQuantity = aggregatedQuantity(item.variant?.sku, fluentStocks);
        return availableQuantity - (item?.count || 0) < 0;
      });
      setIsLoading(false);
      return isInStock;
    } else {
      setIsLoading(true);
      return false;
    }
  }, [cartData?.lineItems, fluentStocks, isStockChecked]);

  const isGiftcardPaymentDataValid = useMemo(() => {
    return (
      cartData?.payments &&
      cartData?.sum &&
      cartData.payments.some((payment) => {
        return payment.paymentProvider === 'giftcard' && cartData?.sum?.centAmount === 0;
      })
    );
  }, [cartData?.payments, cartData?.sum]);

  useEffect(() => {
    setHasOutOfStockItems(isProductsHasOutOfStocks as boolean);
  }, [isProductsHasOutOfStocks]);

  const hasGiftcard = useMemo(() => {
    if (cartData?.lineItems === undefined) {
      return false;
    }

    for (let i = 0; i < cartData.lineItems.length; i++) {
      if (cartData.lineItems[i].isGiftcard === true) {
        return true;
      }
    }
    return false;
  }, [cartData?.lineItems]);

  const isOnlyPerpetualStockItem = useMemo(() => {
    if (cartData?.lineItems) {
      for (let i = 0; i < cartData?.lineItems.length; i++) {
        if (cartData.lineItems[i].isPerpetualStock !== true) {
          return false;
        }
      }
    }
    return true;
  }, [cartData?.lineItems]);

  useEffect(() => {
    if (isActive) {
      if (productSKUs.length > 0) {
        recheckStocks(productSKUs as string[]);
      }
      const checkIfV12Enabled = async () => {
        const flag = await isV12PaymentEnabledForCart();
        setIsV12Enabled(flag);
      };
      checkIfV12Enabled();
    }
  }, [isActive, productSKUs]);

  useEffect(() => {
    if (isActive) {
      const getPaymentMethods = async () => {
        let fetchedPaymentMethods = await adyen.getPaymentMethods(
          cartData?.shippingAddress?.country ?? getCountry(),
          locale,
          cartData?.sum?.currencyCode === 'GBP',
        );

        if (fetchedPaymentMethods) {
          fetchedPaymentMethods = fetchedPaymentMethods.filter((method) => method.type !== 'applepay');

          if (getCurrency() === 'GBP') {
            fetchedPaymentMethods = fetchedPaymentMethods.filter(
              (method) => method.type !== 'ideal' && method.type !== 'klarna_paynow',
            );
          }

          //Below countries are not supported GBP or EUR currency
          if (['CZ', 'DK', 'NO', 'PL', 'RO', 'SE', 'CH'].includes(cartData?.billingAddress?.country ?? 'GB')) {
            fetchedPaymentMethods = fetchedPaymentMethods.filter((method) => method.type.indexOf('klarna') === -1);
          }

          setPaymentMethods(fetchedPaymentMethods);
          // setSelectedPaymentMethod(fetchedPaymentMethods[0]?.type);
          await paymentHelper.freezeCartForPayment(cartData);
        }
      };

      getPaymentMethods();
    }
  }, [isActive]);

  useEffect(() => {
    let mountedComponent = null;
    const initAdyenCheckout = async () => {
      if (PaymentType.V12 === selectedPaymentMethod) {
        return false;
      }

      try {
        if (selectedPaymentMethod && selectedPaymentMethod !== '') {
          setIsLoading(true);
          const sessionRequestData = await paymentHelper.sessionInitializationRequest(cartData);
          setPaymentSession(sessionRequestData);
          const sessionsResponse = await adyen.getPaymentSession(sessionRequestData);
          const checkoutConfig = paymentHelper.getConfig(selectedPaymentMethod, sessionsResponse, router) as any;
          const checkout = await AdyenCheckout(checkoutConfig);
          setAdyenCheckoutInstance(checkout);
          mountedComponent = checkout
            .create(selectedPaymentMethod as string)
            .mount(`#${selectedPaymentMethod}-container`);
          setMountedComponent(mountedComponent);

          if (selectedPaymentMethod.indexOf('klarna') !== -1 || selectedPaymentMethod === 'clearpay') {
            setIsPaymentDataValid(true);
          }
        }
      } catch (error) {
        console.error('Error initializing Adyen Checkout:', error);
      } finally {
        setIsLoading(false);
      }
    };

    setIsPaymentDataValid(false);
    initAdyenCheckout();
  }, [selectedPaymentMethod, cartData]);

  useEffect(() => {
    if (paymentStatus !== '') {
      makeActive();
      setTimeout(() => {
        setProcessing(false);
        if (paymentStatus === '3ds2') scrollToError('3ds2-info');
        else scrollToError('paymentErrorMessage');
      }, 500);
    }
  }, [paymentStatus]);

  const onPaymentMethodChange = (type: string | null) => {
    setSelectedPaymentMethod(type);
    setPreviousPaymentMethod(type);
    if (type !== PaymentType.V12) {
      //if customer selects other payment option, clear the v12 payment data
      setV12PaymentData(null);
    }
  };

  useEffect(() => {
    if (previousPaymentMethod !== null) {
      onPaymentMethodChange(previousPaymentMethod);
    }
  }, [previousPaymentMethod, onPaymentMethodChange]);

  const hidePaymentOptionClass = useMemo(() => {
    return cartData?.sum?.centAmount == 0 ? 'hidden' : '';
  }, [cartData?.sum]);

  function scrollToTop() {
    if (window.innerWidth > 1024) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  const submit = () => {
    const totalAmt = cartData?.sum?.centAmount;
    const hasGivexPayments = cartData?.payments && cartData?.payments?.length > 0 ? true : false;

    const salutation = loggedIn ? account?.salutation || '' : '';
    const currentUrl = document.location.origin;
    const dataLayer = new DataLayerHelpers(
      currentUrl,
      locale,
      cartData as Record<string, any>,
      account?.email,
      account?.accountId,
      salutation,
    );
    const dataLayerData = {
      breadcrumb: 'Checkout / Billing',
      event: 'checkout',
      step: 3,
      option: cartData?.shippingInfo?.name || '',
      store: cartData?.shippingAddress?.streetName || '',
    };

    if (selectedPaymentMethod === PaymentType.V12) {
      dataLayer.setCartDataLayer(dataLayerData);
      goToNextStep();
    } else if (totalAmt == 0 && hasGivexPayments) {
      dataLayer.setCartDataLayer(dataLayerData);
      setPaymentStatus('');
      setSelectedPaymentMethod('');
      goToNextStep();
    } else {
      dataLayer.setCartDataLayer(dataLayerData);
      setPaymentStatus('');
      goToNextStep();
    }

    scrollToTop();
  };

  return (
    <div id="paymentSection " className="lg:px-36 lg:pb-36 lg:pt-0">
      {searchParams.get('ecode') === 'v12rej' && (
        <div id="paymentErrorMessage-v12rej">
          <Message
            severity={'error'}
            message={formatCheckoutMessage({
              id: `payment.v12`,
              defaultMessage:
                'Unfortunately, your  V12 Credit application has been declined. Please select an alternative payment method.',
            })}
          ></Message>
        </div>
      )}
      {paymentStatus !== '' && paymentStatus === 'refused' && searchParams.get('ecode') !== 'v12rej' && (
        <div id="paymentErrorMessage-refused">
          <Message
            severity={'error'}
            message={formatCheckoutMessage({
              id: `payment.refused`,
              defaultMessage: refusedMessage,
            })}
          ></Message>
        </div>
      )}

      {paymentStatus !== '' && paymentStatus === 'cancelled' && (
        <div id="paymentErrorMessage-cancelled">
          <Message
            severity={'error'}
            message={formatCheckoutMessage({
              id: `payment.cancelled`,
              defaultMessage:
                'Unfortunately, we were unable to take payment for this order and no payment has been taken.  Please try again using a different payment method.',
            })}
          ></Message>
        </div>
      )}

      {paymentStatus !== '' && paymentStatus === 'cartTotalError' && (
        <div id="paymentErrorMessage-cartTotalError">
          <Message
            severity={'error'}
            message={formatCheckoutMessage({
              id: `payment.cartTotalError`,
              defaultMessage: refusedMessage,
            })}
          ></Message>
        </div>
      )}

      {paymentStatus !== '' &&
        paymentStatus !== 'refused' &&
        paymentStatus !== '3ds2' &&
        paymentStatus !== 'success' &&
        paymentStatus !== 'cancelled' &&
        paymentStatus !== 'cartTotalError' && (
          <div id="paymentErrorMessage-other">
            <Message
              severity={'error'}
              message={formatCheckoutMessage({
                id: `payment.refused`,
                defaultMessage: refusedMessage,
              })}
            ></Message>
          </div>
        )}
      {/* {!isStockChecked && !isOnlyPerpetualStockItem ? <Loading></Loading> : <></>} */}
      {isStockChecked && !isLoading && hasOutOfStockItems && (
        <div className="mt-24 flex flex-col rounded-xl border border-neutral-400 bg-white px-24 py-16 transition lg:mt-0 lg:bg-white lg:px-36 lg:py-24">
          {paymentStatus !== 'cartTotalError' && (
            <div className="flex cursor-default items-center gap-12 lg:gap-16">
              <Message
                severity="error"
                message={formatCheckoutMessage({
                  id: 'soldOut.error',
                  defaultMessage:
                    "Unfortunately, your cart can't be fulfilled in full at this time as one or more items is unavailable, Please review your cart to continue.",
                })}
              ></Message>
            </div>
          )}
          <div className={` ${paymentStatus !== 'cartTotalError' ? 't-0' : 't-24'}`}>
            <Link link={`/cart`} replace>
              <Button variant="primary" className="w-full min-w-[200px] md:text-14 lg:w-fit lg:px-36">
                {formatCheckoutMessage({ id: 'soldOut.update', defaultMessage: 'Review Cart' })}
              </Button>
            </Link>
          </div>
        </div>
      )}
      <div className={`${!hasOutOfStockItems ? 'block' : 'hidden'}`}>
        {!hasGiftcard && (
          <div className="mb-18 mt-24 rounded-xl border border-[#e6e9eb] bg-white lg:mt-0">
            <Givex></Givex>
          </div>
        )}

        <div
          className={
            'payment-container py-4.5 flex h-full w-full justify-center rounded-xl border border-[#e6e9eb] bg-[#f7f8f9] p-18 md:p-32 ' +
            hidePaymentOptionClass
          }
        >
          <div className="w-full">
            <h2 className="mb-18">Please select your payment method</h2>

            {paymentStatus === '3ds2' && (
              <div id="3ds2-info" className="mt-18">
                <Message
                  severity={'info'}
                  message={formatCheckoutMessage({
                    id: `payment.3ds2`,
                    defaultMessage:
                      "Please complete the 3D Secure verification in the pop-up window to finalize your purchase. It's a quick security step to ensure your transaction is safe.",
                  })}
                ></Message>
              </div>
            )}
            {!paymentMethods &&
              [1, 2, 3].map((item) => (
                <div key={item} className={`cursor-pointer rounded-xl border border-[#e6e9eb] bg-white p-16 lg:p-20`}>
                  <div className="ml-30 flex">
                    <div className="relative mr-12 h-24 w-40">
                      <Skeleton></Skeleton>
                    </div>
                    <div className="relative mt-3 h-18 w-[200px]">
                      <Skeleton></Skeleton>
                    </div>
                  </div>
                </div>
              ))}
            {paymentMethods &&
              paymentMethods.map(({ name, type }) => (
                <div
                  key={type}
                  className={`mb-6 cursor-pointer rounded-xl border border-[#e6e9eb] bg-white p-16 lg:p-20 ${
                    selectedPaymentMethod !== type ? 'bg-white' : 'my-16'
                  }`}
                  onClick={() => onPaymentMethodChange(type)}
                >
                  <div
                    className={`flex items-center justify-between lg:justify-start lg:gap-64 ${
                      selectedPaymentMethod === type &&
                      selectedPaymentMethod.indexOf('klarna') === -1 &&
                      selectedPaymentMethod !== 'clearpay'
                        ? 'mb-20 md:mb-24 '
                        : ''
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-16">
                        <Radio name="checkout-shipping-method" checked={selectedPaymentMethod === type} />
                        <img
                          src={
                            (type && paymentMethodDetails[type]?.image.src) ??
                            `https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/${type}.svg`
                          }
                          className="h-[20px] lg:h-[24px]"
                        />
                        <p className="text-14 font-medium">{(type && paymentMethodDetails[type]?.name) ?? name}</p>
                      </div>
                    </div>
                    {/* eslint-disable-next-line */}
                  </div>
                  <div
                    id={`${type}-container`}
                    style={{
                      display:
                        selectedPaymentMethod === type &&
                        selectedPaymentMethod.indexOf('klarna') === -1 &&
                        selectedPaymentMethod !== 'clearpay'
                          ? 'block'
                          : 'none',
                    }}
                    className={'relative min-h-[80px] md:max-w-[520px]'}
                  >
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60">
                        <div className="relative">
                          <div className="loading-circle"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            {/* V12 direct integration */}

            {isV12Enabled && cartData?.payments && cartData?.payments?.length === 0 && (
              <div
                key="v12"
                className={`cursor-pointer rounded-xl border border-[#e6e9eb] bg-white p-16 lg:p-20 ${
                  selectedPaymentMethod !== PaymentType.V12 ? 'bg-white' : 'my-16'
                }`}
                onClick={() => onPaymentMethodChange(PaymentType.V12)}
              >
                <div
                  className={`flex items-center justify-between lg:justify-start lg:gap-64 ${
                    selectedPaymentMethod === PaymentType.V12 ? 'mb-20 md:mb-24 ' : ''
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-16">
                      <Radio name="checkout-shipping-method" checked={selectedPaymentMethod === PaymentType.V12} />
                      <V12Image />
                      <p className="text-14 font-medium">
                        {(PaymentType.V12 && paymentMethodDetails[PaymentType.V12]?.name) ?? name}
                      </p>
                    </div>
                  </div>
                </div>

                {isV12Enabled && selectedPaymentMethod === PaymentType.V12 && (
                  <div
                    id={`v12-container`}
                    className={'md:max-w-[520px]'}
                    style={{ display: selectedPaymentMethod === PaymentType.V12 ? 'block' : 'none' }}
                  >
                    <V12 setV12PaymentData={setV12PaymentData} formatCheckoutMessage={formatCheckoutMessage}></V12>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {selectedPaymentMethod !== 'paypal' && paymentStatus !== '3ds2' && (
        <div
          className={`mt-24 ${
            (!isLoading && !hasOutOfStockItems) || selectedPaymentMethod !== 'paypal' ? 'block' : 'hidden'
          }`}
        >
          <Button
            variant="primary"
            className="w-full min-w-[200px] md:text-14 lg:w-fit lg:px-36"
            type="submit"
            onClick={submit}
            disabled={hasOutOfStockItems || (!isPaymentDataValid && !isGiftcardPaymentDataValid)}
          >
            {formatCheckoutMessage({ id: 'review.order', defaultMessage: 'Review order' })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Payment;
